import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { notificationActions } from "../../store/slices/notificationSlice";
import { notificationSocketService } from "../../lib/notificationSocket";
import { getUnreadCount } from "../../lib/notifications";
import ToastNotification, { ToastNotificationData } from "./ToastNotification";
import { router } from "expo-router";

const LISTENER_PREFIX = "NotificationManager";

/**
 * Root-level component that:
 * 1. Connects to WebSocket when user is logged in
 * 2. Fetches initial unread count
 * 3. Shows in-app toast for real-time notifications
 * 4. Keeps Redux unreadCount in sync
 *
 * Mount this ONCE inside AppContent (root layout).
 */
const NotificationManager: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [toast, setToast] = useState<ToastNotificationData | null>(null);
  const initialized = useRef(false);

  // Initialise socket + fetch unread count when user is available
  useEffect(() => {
    if (!user?.id) {
      // Clean up if user logs out
      if (initialized.current) {
        notificationSocketService.removeListenersByPrefix(LISTENER_PREFIX);
        notificationSocketService.disconnect();
        dispatch(notificationActions.resetUnreadCount());
        initialized.current = false;
      }
      return;
    }

    // Already initialized for this user
    if (initialized.current) return;
    initialized.current = true;

    // 1. Connect WebSocket
    notificationSocketService.initializeNotificationSocket(String(user.id));

    // 2. Listen for new notifications → show toast + bump unread
    notificationSocketService.onNewNotification((data) => {
      const n = data.notification;
      dispatch(notificationActions.incrementUnreadCount());

      // Show in-app toast
      setToast({
        id: String(n.id_notification || Date.now()),
        title: n.title || "Notification",
        message: n.message || "",
        type: n.type === "alert" ? "warning" : "info",
      });
    }, `${LISTENER_PREFIX}_new`);

    // 3. Listen for unread count broadcasts
    notificationSocketService.onUnreadCountUpdate((data) => {
      dispatch(notificationActions.setUnreadCount(data.unreadCount));
    }, `${LISTENER_PREFIX}_count`);

    // 4. Fetch initial unread count from REST
    getUnreadCount().then(({ unreadCount }) => {
      dispatch(notificationActions.setUnreadCount(unreadCount));
    });

    return () => {
      notificationSocketService.removeListenersByPrefix(LISTENER_PREFIX);
    };
  }, [user?.id, dispatch]);

  const handleToastPress = useCallback(
    (notification: ToastNotificationData) => {
      router.push("/(drawer)/notifications" as any);
    },
    [],
  );

  return (
    <ToastNotification
      notification={toast}
      onDismiss={() => setToast(null)}
      onPress={handleToastPress}
    />
  );
};

export default NotificationManager;
